import isEqual from 'lodash.isequal';
import { useContext, useEffect, useMemo, useState } from 'react';
import TagId from '~/constants/common/TagId';
import { COOKIES } from '~/constants/srp';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import StaticContext from '~/context/srp/StaticContext';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import useHasPriceDropFacetCount from '~/hooks/srp/selectors/useHasPriceDropFacetCount';
import decodeCvnaId from '~/utilities/cvnaId';
import { Dismissed, NewPriceFilter, PromoBanners } from '../constants';
import { setBannerDismissedCookie } from '../utils';

export default function useHandleShowLogic() {
  const { dismissedNewPrice } = useContext(StaticContext);
  const savedSearches = useSavedSearches();
  const {
    appliedFilters: { tagIds }
  } = useContext(AppliedFiltersContext);
  const [isPriceDropDismissed, setIsPriceDropDismissed] =
    useState(dismissedNewPrice);
  const hasPriceDropCount = useHasPriceDropFacetCount();
  const isInPriceDropSuppressionExperiment =
    useIsInPriceDropSuppressionExperiment();

  const hasNewPriceFilter = useMemo(
    () => tagIds?.includes(TagId.PriceDrop.toString()) ?? false,
    [tagIds]
  );

  const hasNewPriceSavedSearch = useMemo(
    () =>
      savedSearches.some(savedSearch =>
        isEqual(decodeCvnaId(savedSearch.cvnaId).filters, NewPriceFilter)
      ),
    [savedSearches]
  );

  const shouldDismissPriceDrop = hasNewPriceFilter || hasNewPriceSavedSearch;

  useEffect(() => {
    if (shouldDismissPriceDrop) {
      if (hasNewPriceSavedSearch) {
        setBannerDismissedCookie(
          PromoBanners.PriceDrop,
          COOKIES.dismissedNewPriceBanner
        );
      }

      setIsPriceDropDismissed(Dismissed.Banner);
    }
  }, [hasNewPriceFilter, hasNewPriceSavedSearch, shouldDismissPriceDrop]);

  const shouldShow = useMemo(
    () =>
      isPriceDropDismissed === Dismissed.None &&
      !shouldDismissPriceDrop &&
      hasPriceDropCount &&
      !isInPriceDropSuppressionExperiment,
    [
      hasPriceDropCount,
      isInPriceDropSuppressionExperiment,
      isPriceDropDismissed,
      shouldDismissPriceDrop
    ]
  );

  return {
    setIsPriceDropDismissed,
    shouldShow
  };
}
